import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Container, Row, Col, Table, Alert, Spinner, Card, Button, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import EditCliente from '../components/EditCliente';
import { ThemeContext } from '../context/ThemeContext';
import { DestinazioneContext } from '../context/DestinazioneContext';
import { useAlert } from '../context/AlertContext';
import configService from '../services/configService';
import clientiService from '../services/clientiService';
import useIsMobile from '../hooks/useIsMobile';

const Destinazioni = () => {
  const { darkMode } = useContext(ThemeContext);
  const { loginDestinazione } = useContext(DestinazioneContext);
  const { showAlertGlobal } = useAlert();
  const navigate = useNavigate();
  const [destinazioni, setDestinazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [codiceFilter, setCodiceFilter] = useState('');
  const isMobile = useIsMobile();

  const [showClienteModal, setShowClienteModal] = useState(false);
  const [clienteDettagli, setClienteDettagli] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [isNewCliente, setIsNewCliente] = useState(false);

  const loadDestinazioni = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await configService.getDestinazioni();
      
      // Debug: visualizza la struttura dei dati per verificare i campi
      console.log('Destinazioni caricate:', data);
      if (data && data.length > 0) {
        console.log('Esempio prima destinazione:', data[0]);
        console.log('Campi orderactive disponibili:', data.map(d => ({ id: d.id, orderactive: d.orderactive, tipo: typeof d.orderactive })));
      }
      
      // Ordina le destinazioni: prima quelle con orderactive = 1, poi le altre
      const sortedData = data.sort((a, b) => {
        const aActive = (a.orderactive === 1 || a.orderactive === '1' || a.orderactive === true) ? 1 : 0;
        const bActive = (b.orderactive === 1 || b.orderactive === '1' || b.orderactive === true) ? 1 : 0;
        
        // Ordina per orderactive in ordine decrescente (1 prima di 0)
        if (aActive !== bActive) {
          return bActive - aActive;
        }
        
        // Se hanno lo stesso stato orderactive, ordina per nome destinazione
        const nameA = (a.nomede || '').toLowerCase();
        const nameB = (b.nomede || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setDestinazioni(sortedData);
    } catch (error) {
      console.error('Errore nel caricamento delle destinazioni:', error);
      setError('Errore nel caricamento delle destinazioni');
      showAlertGlobal('Errore nel caricamento delle destinazioni', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showAlertGlobal]);

  const formatIndirizzo = (destinazione) => {
    const parts = [];
    if (destinazione.viade) parts.push(destinazione.viade);
    if (destinazione.cittade) parts.push(destinazione.cittade);
    if (destinazione.provinciade) parts.push(destinazione.provinciade);
    if (destinazione.capde) parts.push(destinazione.capde);
    
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  // Destinazioni filtrate in base al termine di ricerca
  const destinazioniFiltrate = useMemo(() => {
    let filtered = destinazioni;
    
    // Filtra per nome destinazione
    if (searchFilter.trim()) {
      const filtroNome = searchFilter.toLowerCase();
      filtered = filtered.filter(destinazione => 
        (destinazione.nomede || '').toLowerCase().includes(filtroNome) ||
        formatIndirizzo(destinazione).toLowerCase().includes(filtroNome) ||
        (destinazione.destlogin || '').toLowerCase().includes(filtroNome)
      );
    }
    
    // Filtra per login
    if (codiceFilter.trim()) {
      const filtroLogin = codiceFilter.toLowerCase();
      filtered = filtered.filter(destinazione => 
        (destinazione.destlogin || '').toLowerCase().includes(filtroLogin)
      );
    }
    
    return filtered;
  }, [destinazioni, searchFilter, codiceFilter]);

  const handleLogin = async (destinazione) => {
    if (!destinazione.destlogin || !destinazione.destpsw) {
      showAlertGlobal('Credenziali mancanti per questa destinazione', 'warning');
      return;
    }

    setLoginLoading(destinazione.id);
    
    try {
      console.log("[Destinazioni] Inizio login per destinazione:", destinazione.nomede);
      const result = await loginDestinazione(
        destinazione.destlogin,
        destinazione.destpsw,
        null,
        { preserveAdminRole: true }
      );
      
      console.log("[Destinazioni] Risultato login:", result);
      
      if (result && result.success) {
        showAlertGlobal(`Login effettuato con successo per ${destinazione.nomede}`, 'success');
        
        // Emetti evento personalizzato per aggiornare il SideMenu
        window.dispatchEvent(new Event('destinazioneChanged'));
        
        // Piccolo delay per assicurare che il localStorage sia aggiornato
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Prova multiple strategie di navigazione
        console.log("[Destinazioni] Tentativo navigazione verso /home");
        
        // Strategia 1: navigate di React Router
        try {
          navigate("/home", { replace: true });
          console.log("[Destinazioni] Navigate() eseguito con successo");
        } catch (navError) {
          console.error("[Destinazioni] Errore con navigate():", navError);
          
          // Strategia 2: window.location come fallback
          console.log("[Destinazioni] Fallback a window.location");
          window.location.href = window.location.origin + "/home";
        }
      } else {
        console.error("[Destinazioni] Login fallito, risultato:", result);
        showAlertGlobal('Login fallito. Controlla le credenziali.', 'danger');
      }
    } catch (error) {
      console.error('Errore durante il login:', error);
      showAlertGlobal('Errore durante il login', 'danger');
    } finally {
      setLoginLoading(null);
    }
  };

  const handleModificaCliente = async (destinazione) => {
    console.log('Modifica cliente:', destinazione);
    
    setIsNewCliente(false); // Reset modalità nuovo cliente
    
    // Estrai il codice cliente dal campo primario usando substr(primario, 2, 4)
    if (!destinazione.primario) {
      showAlertGlobal('Campo primario non trovato per questa destinazione', 'warning');
      return;
    }
    
    // Usa substr(primario, 2, 4) per estrarre il codice cliente
    const codiceCliente = destinazione.primario.substr(2, 4);
    console.log('Codice cliente estratto da primario:', codiceCliente, 'da:', destinazione.primario);
    
    if (!codiceCliente || codiceCliente.length === 0) {
      showAlertGlobal('Impossibile estrarre il codice cliente dal campo primario', 'warning');
      return;
    }
    
    setLoadingCliente(true);
    try {
      console.log('Caricamento dati cliente:', codiceCliente);
      const clienteData = await clientiService.getClienti(codiceCliente);
      
      console.log('Dati cliente caricati:', clienteData);
      setClienteDettagli(clienteData);
      setShowClienteModal(true);
      
    } catch (error) {
      console.error('Errore nel caricamento dei dati cliente:', error);
      showAlertGlobal('Errore nel caricamento dei dati del cliente', 'danger');
    } finally {
      setLoadingCliente(false);
    }
  };

  const handleNuovaDestinazione = () => {
    console.log('Nuovo cliente richiesto');
    setIsNewCliente(true);
    setClienteDettagli(null);
    setShowClienteModal(true);
  };

  useEffect(() => {
    loadDestinazioni();
  }, [loadDestinazioni]);

  const formatField = (value) => {
    return value || '-';
  };

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      <Navbar />
      
      {/* RENDERING CONDIZIONALE BASATO SU JAVASCRIPT */}
      {!isMobile ? (
        // ===== VERSIONE DESKTOP =====
        <Container fluid className="mt-5 pt-3">
          <Row className="justify-content-center">
            <Col xs={12}>
              
              {/* Header con stesso stile di Preordini */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className={darkMode ? 'text-light' : 'text-dark'}>
                    <i className="fas fa-map-marker-alt me-2"></i>
                    Lista Destinazioni
                  </h2>
                </div>
                <div>
                  <Button
                    variant="primary"
                    onClick={handleNuovaDestinazione}
                    className="d-flex align-items-center"
                  >
                    <i className="fas fa-plus me-2"></i>
                    Nuovo Cliente
                  </Button>
                </div>
              </div>

              {/* Card Filtri identica a Preordini */}
              <Card className="mb-4">
                <Card.Header className="card-header-sidebar-bg">
                  <h5 className="mb-0">
                    <i className="fas fa-filter me-2"></i>Filtri
                  </h5>
                </Card.Header>
                <Card.Body className={darkMode ? 'text-light' : ''}>
                  <Row className="g-3">
                    {/* Filtro per descrizione */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Filtra per Descrizione</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className={darkMode ? "bg-dark text-light border-secondary" : ""}>
                            <i className="fas fa-search"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cerca per nome destinazione..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className={darkMode ? "bg-dark text-light border-secondary" : ""}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    {/* Filtro per codice */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Filtra per Login</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className={darkMode ? "bg-dark text-light border-secondary" : ""}>
                            <i className="fas fa-user"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cerca per login..."
                            value={codiceFilter}
                            onChange={(e) => setCodiceFilter(e.target.value)}
                            className={darkMode ? "bg-dark text-light border-secondary" : ""}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mt-3">
                    <Col>
                      <Button 
                        variant="primary" 
                        onClick={loadDestinazioni}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt me-2"></i>
                            Aggiorna Destinazioni
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Contenuto principale con struttura identica a Preordini */}
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <p>Caricamento destinazioni in corso...</p>
                  </div>
                </div>
              ) : error ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              ) : (
                <Card>
                  <Card.Header className="card-header-sidebar-bg">
                    <h5 className="mb-0">
                      <i className="fas fa-list me-2"></i>
                      Destinazioni Disponibili ({destinazioniFiltrate.length})
                    </h5>
                  </Card.Header>
                  <Card.Body className={darkMode ? 'text-light' : ''}>
                    {destinazioniFiltrate.length === 0 ? (
                      <div className="text-center py-4">
                        <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                        <p className="text-muted">Nessuna destinazione trovata per i criteri selezionati.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table 
                          striped 
                          bordered 
                          hover 
                          className={darkMode ? 'table-dark' : ''}
                        >
                          <thead className={darkMode ? 'table-dark' : 'table-light'}>
                            <tr>
                              <th>Nome Destinazione</th>
                              <th>Login</th>
                              <th>Indirizzo</th>
                              <th>Stato</th>
                              <th>Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {destinazioniFiltrate.map((destinazione, index) => (
                              <tr key={destinazione.id || index}>
                                <td>
                                  <strong>{formatField(destinazione.nomede)}</strong>
                                </td>
                                <td>
                                  <code className={`small ${darkMode ? 'text-warning' : 'text-primary'}`}>
                                    {formatField(destinazione.destlogin)}
                                  </code>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {formatIndirizzo(destinazione)}
                                  </small>
                                </td>
                                <td>
                                  {(destinazione.orderactive === 1 || destinazione.orderactive === '1' || destinazione.orderactive === true) ? (
                                    <span className="badge bg-success">Attiva</span>
                                  ) : (
                                    <span className="badge bg-secondary">Non attiva</span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2 align-items-center">
                                    {(destinazione.orderactive === 1 || destinazione.orderactive === '1' || destinazione.orderactive === true) ? (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleLogin(destinazione)}
                                        disabled={loginLoading === destinazione.id}
                                        className="d-flex align-items-center"
                                      >
                                        {loginLoading === destinazione.id ? (
                                          <>
                                            <Spinner size="sm" className="me-1" />
                                            <span>Accesso...</span>
                                          </>
                                        ) : (
                                          <>
                                            <i className="fas fa-sign-in-alt me-1"></i>
                                            <span>Accedi</span>
                                          </>
                                        )}
                                      </Button>
                                    ) : (
                                      <Button variant="outline-secondary" size="sm" disabled>
                                        <i className="fas fa-ban me-1"></i>
                                        Non disponibile
                                      </Button>
                                    )}
                                    
                                    {/* Pulsante Modifica Cliente */}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleModificaCliente(destinazione)}
                                      className="d-flex align-items-center"
                                      title="Modifica dati cliente"
                                    >
                                      <i className="fas fa-edit me-1"></i>
                                      <span>Modifica</span>
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

            </Col>
          </Row>
        </Container>
      ) : (
        // ===== VERSIONE MOBILE: header + filtri fissi, solo cards scrollabili =====
        <div
          className="destinazioni-mobile-shell"
          style={{
            backgroundColor: darkMode ? '#121212' : '#FFFEF9',
          }}
        >
          {/* Ancorato in alto: titolo, Nuova, campi ricerca */}
          <div
            className="destinazioni-mobile-sticky px-3 pt-2 pb-2"
            style={{
              backgroundColor: darkMode ? '#121212' : '#FFFEF9',
              borderBottom: `1px solid ${darkMode ? '#495057' : '#dee2e6'}`,
              boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <div className={`d-flex justify-content-between align-items-center mb-2 ${darkMode ? 'text-light' : 'text-dark'}`}>
              <h4 className="mb-0">
                <i className="fas fa-map-marker-alt me-2"></i>
                Destinazioni
              </h4>
              <Button
                variant="primary"
                onClick={handleNuovaDestinazione}
                className="d-flex align-items-center"
              >
                <i className="fas fa-plus me-1"></i>
                Nuova
              </Button>
            </div>

            <InputGroup className="mb-2">
              <InputGroup.Text className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
                <i className="fas fa-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Cerca nome, login o indirizzo..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
              />
              {searchFilter && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchFilter('')}
                  style={{ borderLeft: 'none' }}
                >
                  <i className="fas fa-times"></i>
                </Button>
              )}
            </InputGroup>

            {(destinazioniFiltrate.length !== destinazioni.length) && (
              <div className="text-muted mt-2 text-center">
                <small>
                  {destinazioniFiltrate.length} di {destinazioni.length} destinazioni
                  {(searchFilter || codiceFilter) && (
                    <span className="d-block">
                      <i className="fas fa-filter me-1"></i>
                      Filtri attivi:
                      {searchFilter && <span className="ms-1 badge bg-primary">Nome</span>}
                      {codiceFilter && <span className="ms-1 badge bg-info">Login</span>}
                    </span>
                  )}
                </small>
              </div>
            )}
          </div>

          {/* Solo questa area scrolla (cards) */}
          <div className="destinazioni-mobile-scroll px-3 py-2">
            {loading && (
              <div className="text-center my-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </Spinner>
                <p className="mt-2">Caricamento destinazioni...</p>
              </div>
            )}

            {error && (
              <Alert variant="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {!loading && !error && (
              <>
                {destinazioni.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    Nessuna destinazione trovata
                  </Alert>
                ) : destinazioniFiltrate.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    Nessuna destinazione per i filtri selezionati
                  </Alert>
                ) : (
                  <>
                    {destinazioniFiltrate.map((destinazione, index) => (
                      <Card key={destinazione.id || index} className={`mb-2 ${darkMode ? 'text-light' : ''}`}>
                        <Card.Body className="p-3">
                          <h6 className="mb-1" style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>
                            {formatField(destinazione.nomede)}
                          </h6>
                          <div className="mb-1">
                            <small>
                              <strong>Login:</strong>{' '}
                              <code className={darkMode ? 'text-warning' : 'text-primary'}>
                                {formatField(destinazione.destlogin)}
                              </code>
                            </small>
                          </div>
                          <div className="mb-1 text-muted">
                            <small>{formatIndirizzo(destinazione)}</small>
                          </div>
                          <div className="mb-3">
                            {(destinazione.orderactive === 1 || destinazione.orderactive === '1' || destinazione.orderactive === true) ? (
                              <span className="badge bg-success">Attiva</span>
                            ) : (
                              <span className="badge bg-secondary">Non attiva</span>
                            )}
                          </div>
                          <div className="d-flex gap-2">
                            {(destinazione.orderactive === 1 || destinazione.orderactive === '1' || destinazione.orderactive === true) ? (
                              <Button
                                variant="success"
                                className="flex-fill mobile-list-action-btn"
                                onClick={() => handleLogin(destinazione)}
                                disabled={loginLoading === destinazione.id}
                              >
                                {loginLoading === destinazione.id ? (
                                  <>
                                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                                    Accesso...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-sign-in-alt me-1"></i>
                                    Accedi
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button variant="outline-secondary" className="flex-fill mobile-list-action-btn" disabled>
                                Non attivo
                              </Button>
                            )}
                            <Button
                              variant="outline-primary"
                              className="flex-fill mobile-list-action-btn"
                              onClick={() => handleModificaCliente(destinazione)}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Modifica
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}

                    <div className={`mt-2 mb-3 p-3 rounded ${darkMode ? 'bg-secondary text-light' : 'bg-light text-muted'}`}>
                      <div className="text-center">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          <strong>Totale: {destinazioni.length}</strong>
                          {destinazioni.filter(d => d.orderactive === 1 || d.orderactive === '1' || d.orderactive === true).length > 0 && (
                            <span className="ms-2">
                              <i className="fas fa-check-circle me-1 text-success"></i>
                              Attive: {destinazioni.filter(d => d.orderactive === 1 || d.orderactive === '1' || d.orderactive === true).length}
                            </span>
                          )}
                        </small>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Componente EditCliente */}
      <EditCliente
        show={showClienteModal}
        onHide={() => {
          setShowClienteModal(false);
          setIsNewCliente(false); // Reset modalità quando si chiude
        }}
        clienteDettagli={clienteDettagli}
        loadingCliente={loadingCliente}
        isNewCliente={isNewCliente}
        onClienteUpdate={(clienteAggiornato) => {
          if (clienteAggiornato != null) {
            setClienteDettagli(clienteAggiornato);
          }
          // Sempre: nuovo cliente o modifica (il toast è già in EditCliente)
          loadDestinazioni();
        }}
      />
    </div>
  );
};

export default Destinazioni;
